import { useNotificationStore } from '@/stores/notification';
import { usePortalMessages } from '@/composables/usePortalMessages';

/**
 * The snackbar a client sees when a message arrives while they are in the portal (#5334).
 *
 * A composable rather than a handler inside DefaultLayout because the rule below is worth testing
 * on its own — the layout is a 700-line component with a nav drawer, an app bar and a stream
 * subscription, and none of that has anything to say about whether a snackbar should appear.
 *
 * THE TEXT NAMES NOTHING. No sender, no conversation, no content — the stream event carries none
 * of those anyway (see MessageSendService::announce()), and a snackbar is on screen in whatever
 * room the client is sitting in. It says a message arrived; reading it is a deliberate act.
 *
 * This does NOT replace the email. #5334's criterion 2 asks for the snackbar "instead of" the
 * email for a client currently in the portal, and the suppression half is not implemented —
 * see MessageNotificationService's docblock for the two mechanisms this deployment lacks. So a
 * client reading in the portal gets both, which is redundant rather than wrong.
 */

// Deliberately a constant: the notification store dedupes by message text, so two messages
// arriving before the client dismisses the first produce ONE snackbar rather than a stack of
// identical ones. That is the desired behaviour, and it only works while the text is stable.
export const NEW_MESSAGE_NOTIFICATION = 'You have a new message.';

export function usePortalMessageNotifications() {
    const notificationStore = useNotificationStore();
    const { activeThreadId } = usePortalMessages();

    /**
     * Turn one stream event into (at most) one snackbar.
     *
     * A conversation the client is looking at is skipped: the message is already appearing on
     * screen through PortalMessagesPage's own handler, and a snackbar announcing something the
     * reader can see is noise. Every other event type is ignored — reactions, edits and read
     * receipts are staff capabilities the portal does not offer.
     */
    const handleStreamEvent = (event) => {
        if (event?.type !== 'message.created') return;
        if (event.threadId && event.threadId === activeThreadId.value) return;

        notificationStore.add({ message: NEW_MESSAGE_NOTIFICATION, color: 'info' });
    };

    return { handleStreamEvent };
}

export default usePortalMessageNotifications;
